import React, {useContext, useEffect, useRef} from 'react';
import { sdk } from '@farcaster/miniapp-sdk';

import {BrowserRouter as Router, Route, Routes, Navigate} from 'react-router-dom';
import {AuthProvider} from './context/AuthContext';
import {SwapMainAppProvider, SwapMainAppContext} from "./context/SwapMainAppContext";
import AdminPortal from "./components/Admin/AdminPortal";
import RecaptchaProvider from "./utils/RecaptchaProvider";
import {HelmetProvider} from 'react-helmet-async';
import GoogleAnalytics from './analytics/GoogleAnalytics';
import Home from "./components/Home/Home";
import './App.css';

import {getRpcCallCount, isBetaEnvironment} from "./configs/config";
import ReferralLandingPage from "./components/InviteFriends/ReferralLandingPage";
import BetaAccessGate from './components/header/BetaAccessGate';

const BaseMiniAppEnvironmentNotifier = () => {
    const swapContext = useContext(SwapMainAppContext);
    const showNotificationRef = useRef(swapContext?.showNotification);

    useEffect(() => {
        showNotificationRef.current = swapContext?.showNotification;
    }, [swapContext?.showNotification]);

    useEffect(() => {
        let isMounted = true;

        const notify = (status, message, duration = 8000) => {
            if (typeof showNotificationRef.current === 'function') {
                showNotificationRef.current(status, message, duration);
            } else {
                console.log(`[${status}] ${message}`);
            }
        };

        const detectEnvironment = async () => {
            try {
                const isBaseMiniApp = await sdk.isInMiniApp();

                if (!isBaseMiniApp || !isMounted) {
                    return;
                }

                await sdk.actions.ready();
                const context = await sdk.context;

                if (!isMounted) {
                    return;
                }
            } catch (error) {
                notify(
                    'error',
                    `Base mini app detection failed: ${error instanceof Error ? error.message : String(error)}`
                );
            }
        };

        detectEnvironment();

        return () => {
            isMounted = false;
        };
    }, []);

    return null;
};

const App = () => {
    console.log("Total RPC calls so far:", getRpcCallCount());

    return (
        <HelmetProvider>
            <RecaptchaProvider>
                <SwapMainAppProvider>
                    <BaseMiniAppEnvironmentNotifier />
                    <AuthProvider>
                        { isBetaEnvironment && <BetaAccessGate /> }
                        <Router>
                            <GoogleAnalytics />
                            <Routes>
                                <Route path="/email" element={<Navigate to="/" replace/>}/>
                                <Route path="/admin" element={<AdminPortal/>}/>
                                <Route
                                    path="/invite-friend/:referralCode"
                                    element={<ReferralLandingPage/>}
                                />
                                <Route path="/*" element={<Home/>}/>
                                <Route path="*" element={<Navigate to="/"/>}/>
                            </Routes>
                        </Router>
                    </AuthProvider>
                </SwapMainAppProvider>
            </RecaptchaProvider>
        </HelmetProvider>
    );
};

export default App;
